const DEFAULT_LIGHT_PALETTE = {
  background: "#fffdf8",
  surface: "#f5f0ff",
  surfaceStrong: "#ffffff",
  text: "#171717",
  textMuted: "#4b5563",
  primary: "#6e46d6",
  primaryForeground: "#ffffff",
  accent: "#b77900",
  accentForeground: "#ffffff",
  border: "#ddd6ef",
  input: "#faf7ff",
  ring: "#6e46d6",
  success: "#16a34a",
  warning: "#d97706",
  error: "#dc2626",
};

const DEFAULT_DARK_PALETTE = {
  background: "#050505",
  surface: "#111111",
  surfaceStrong: "#18181b",
  text: "#ffffff",
  textMuted: "#a1a1aa",
  primary: "#fccf1f",
  primaryForeground: "#000000",
  accent: "#fccf1f",
  accentForeground: "#000000",
  border: "#27272a",
  input: "#18181b",
  ring: "#fccf1f",
  success: "#22c55e",
  warning: "#f59e0b",
  error: "#ef4444",
};

const clampByte = (value) => Math.max(0, Math.min(255, Math.round(value)));

const normalizeHex = (value, fallback) => {
  if (typeof value !== "string") return fallback;
  const raw = value.trim();
  const short = /^#?([0-9a-f]{3})$/i.exec(raw);
  if (short) {
    return `#${short[1].split("").map((ch) => ch + ch).join("").toLowerCase()}`;
  }
  const long = /^#?([0-9a-f]{6})$/i.exec(raw);
  if (long) {
    return `#${long[1].toLowerCase()}`;
  }
  return fallback;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHex(hex, "#000000").replace("#", "");
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const rgbToHslComponents = ({ r, g, b }) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  let hue = 0;
  let saturation = 0;

  if (max !== min) {
    const delta = max - min;
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    switch (max) {
      case red:
        hue = (green - blue) / delta + (green < blue ? 6 : 0);
        break;
      case green:
        hue = (blue - red) / delta + 2;
        break;
      default:
        hue = (red - green) / delta + 4;
        break;
    }
    hue /= 6;
  }

  return `${Math.round(hue * 360)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
};

const withAlpha = ({ r, g, b }, alpha) => `rgba(${r}, ${g}, ${b}, ${alpha})`;

const adjustRgb = ({ r, g, b }, amount) => ({
  r: clampByte(r + amount),
  g: clampByte(g + amount),
  b: clampByte(b + amount),
});

const mergePalette = (palette, defaults) => {
  const normalized = {};
  Object.entries(defaults).forEach(([key, fallback]) => {
    normalized[key] = normalizeHex(palette?.[key], fallback);
  });
  return normalized;
};

export const normalizeThemePalettes = (colors) => ({
  light: mergePalette(colors?.light, DEFAULT_LIGHT_PALETTE),
  dark: mergePalette(colors?.dark, DEFAULT_DARK_PALETTE),
});

export const applyThemePalettes = (colors) => {
  if (typeof document === "undefined") return;

  const palettes = normalizeThemePalettes(colors);
  const root = document.documentElement;

  Object.entries(palettes).forEach(([mode, palette]) => {
    Object.entries(palette).forEach(([key, hex]) => {
      const rgb = hexToRgb(hex);
      const kebabKey = key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
      root.style.setProperty(`--theme-${mode}-${kebabKey}`, hex);
      root.style.setProperty(`--theme-${mode}-${kebabKey}-rgb`, `${rgb.r} ${rgb.g} ${rgb.b}`);
      root.style.setProperty(`--theme-${mode}-${kebabKey}-hsl`, rgbToHslComponents(rgb));
    });

    const primaryRgb = hexToRgb(palette.primary);
    const accentRgb = hexToRgb(palette.accent);
    const surfaceRgb = hexToRgb(palette.surface);
    const borderRgb = hexToRgb(palette.border);
    const primaryStrong = adjustRgb(primaryRgb, mode === "light" ? -24 : -16);
    const accentStrong = adjustRgb(accentRgb, mode === "light" ? -24 : -16);

    root.style.setProperty(`--theme-${mode}-primary-soft`, withAlpha(primaryRgb, mode === "light" ? 0.12 : 0.18));
    root.style.setProperty(`--theme-${mode}-primary-panel`, withAlpha(primaryRgb, mode === "light" ? 0.10 : 0.14));
    root.style.setProperty(`--theme-${mode}-primary-panel-strong`, withAlpha(primaryRgb, mode === "light" ? 0.16 : 0.22));
    root.style.setProperty(`--theme-${mode}-primary-strong`, `rgb(${primaryStrong.r} ${primaryStrong.g} ${primaryStrong.b})`);
    root.style.setProperty(`--theme-${mode}-accent-soft`, withAlpha(accentRgb, mode === "light" ? 0.12 : 0.16));
    root.style.setProperty(`--theme-${mode}-accent-strong`, `rgb(${accentStrong.r} ${accentStrong.g} ${accentStrong.b})`);
    root.style.setProperty(`--theme-${mode}-surface-alpha`, withAlpha(surfaceRgb, mode === "light" ? 0.78 : 0.94));
    root.style.setProperty(`--theme-${mode}-surface-strong-alpha`, withAlpha(surfaceRgb, mode === "light" ? 0.94 : 0.98));
    root.style.setProperty(`--theme-${mode}-border-soft`, withAlpha(borderRgb, mode === "light" ? 0.12 : 0.24));
  });
};

export const getThemePaletteDefaults = () => ({
  light: { ...DEFAULT_LIGHT_PALETTE },
  dark: { ...DEFAULT_DARK_PALETTE },
});
